// Trimming an overlong lifting session (#101).
//
// validateAndAdjustTiming only scales ACTIVE TIMED work: reps and the rest
// between heavy sets are held fixed (#63), and it leaves anything within 15%
// alone. So a lifting session the model builds too long stays too long; the
// #87 evaluation found 45 min strength sessions landing at 49. This takes the
// overrun out of the accessories instead, a set at a time:
//
//   1. one set off each accessory in turn, last exercise first, never below
//      MIN_SETS and never the first (main) lift;
//   2. if that still isn't enough, drop the last accessory exercise, keeping
//      the main lift and at least one other.
//
// It stops as soon as the session is within OVERRUN_TOLERANCE of the target.
// Timed blocks (intervals, EMOMs, Tabata, AMRAPs), warm-ups and cool-downs are
// never touched.

export const OVERRUN_TOLERANCE = 0.05;
export const MIN_SETS = 2;

const WARM_COOL_RE = /warm[\s-]?up|cool[\s-]?down/i;
const PROTOCOL_RE = /tabata|\be\d*mom\b|amrap/i;

const isRepsWork = (n) => n?.type === "Work" && String(n.unit || "").toLowerCase() === "reps";

// A straight-sets lifting group: "sets" Group whose Work is counted in reps.
const isLiftingGroup = (n) =>
  n?.type === "Group" &&
  String(n.unit || "").toLowerCase() === "sets" &&
  (n.children || []).some(isRepsWork);

/** Lifting groups in plan order, each with the parent that holds it. */
export const findLiftingGroups = (plan) => {
  const found = [];
  const walk = (node, parent) => {
    if (!node || typeof node !== "object") return;
    if (node !== plan) {
      if (/warmup|cooldown/i.test(node.type || "")) return;
      if (node.type === "Group" && (WARM_COOL_RE.test(node.label || "") || PROTOCOL_RE.test(node.label || ""))) return;
    }
    if (isLiftingGroup(node)) {
      found.push({ group: node, parent });
      return;
    }
    (node.children || []).forEach((child) => walk(child, node));
  };
  walk(plan, null);
  return found;
};

// One set fewer, keeping any per-set scheme the same length as the set count.
const dropOneSet = (group) => {
  group.value = Number(group.value) - 1;
  for (const child of group.children || []) {
    if (!child?.setScheme) continue;
    for (const key of Object.keys(child.setScheme)) {
      if (Array.isArray(child.setScheme[key])) {
        child.setScheme[key] = child.setScheme[key].slice(0, group.value);
      }
    }
  }
};

// Remove a group and the changeover that led into it (or out of it, if it led
// the block), so no orphaned "Setup" is left behind.
const removeGroup = ({ group, parent }) => {
  const siblings = parent?.children;
  if (!Array.isArray(siblings)) return false;
  const i = siblings.indexOf(group);
  if (i < 0) return false;
  const before = siblings[i - 1];
  const after = siblings[i + 1];
  if (before?.type === "Transition") siblings.splice(i - 1, 2);
  else if (after?.type === "Transition") siblings.splice(i, 2);
  else siblings.splice(i, 1);
  return true;
};

/**
 * Trim accessory sets (then accessories) until the plan fits the target.
 * Mutates `plan`. `durationOf(plan)` returns its length in seconds.
 *
 * @returns {{ before: number, after: number, setsRemoved: number, exercisesRemoved: string[] }}
 */
export const trimLiftingSets = (plan, targetSeconds, durationOf) => {
  const before = durationOf(plan);
  const limit = targetSeconds * (1 + OVERRUN_TOLERANCE);
  const result = { before, after: before, setsRemoved: 0, exercisesRemoved: [] };
  if (!(targetSeconds > 0) || before <= limit) return result;

  const groups = findLiftingGroups(plan);
  if (groups.length < 2) return result;
  const accessories = groups.slice(1).reverse(); // last exercise first

  let total = before;
  let changed = true;
  while (total > limit && changed) {
    changed = false;
    for (const { group } of accessories) {
      if (total <= limit) break;
      if (Number(group.value) > MIN_SETS) {
        dropOneSet(group);
        result.setsRemoved += 1;
        total = durationOf(plan);
        changed = true;
      }
    }
  }

  // Still over: drop whole accessories, last first, keeping the main lift and one more.
  let remaining = groups.length;
  for (const entry of accessories) {
    if (total <= limit || remaining <= 2) break;
    if (removeGroup(entry)) {
      result.exercisesRemoved.push(entry.group.label || "exercise");
      remaining -= 1;
      total = durationOf(plan);
    }
  }

  result.after = total;
  return result;
};
